/**
 * Playbook Service
 * ================
 * Client for managing trading playbooks and their rules.
 */

import { getApiUrl } from '../api/config';

const getBaseUrl = () => `${getApiUrl()}/playbook`;

// ─── PLAYBOOK CRUD ───────────────────────────────────────────

export async function getPlaybooks() {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch playbooks');
  }

  return response.json();
}

export async function createPlaybook({ name, description }) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ name, description }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create playbook');
  }

  return response.json();
}

export async function updatePlaybook(id, { name, description }) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ name, description }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update playbook');
  }

  return response.json();
}

export async function deletePlaybook(id) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete playbook');
  }

  return response.json();
}

// ─── RULES CRUD ──────────────────────────────────────────────

export async function getRules(playbookId) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${playbookId}/rules`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch rules');
  }

  return response.json();
}

export async function createRule(playbookId, { label, description }) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${playbookId}/rules`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ label, description }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create rule');
  }

  return response.json();
}

export async function updateRule(playbookId, ruleId, updates) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${playbookId}/rules/${ruleId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update rule');
  }

  return response.json();
}

export async function deleteRule(playbookId, ruleId) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/playbooks/${playbookId}/rules/${ruleId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete rule');
  }

  return response.json();
}

// ─── ANALYTICS ───────────────────────────────────────────────

export async function getPlaybookAnalytics(widget, playbookId = null) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/analytics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ widget, playbookId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch playbook analytics');
  }

  return response.json();
}
